import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api, AUTH_INVALID_EVENT, setAccessTokenProvider } from '../api/apiService';
import { authEnabled, initializeKeycloak, keycloakClient } from './keycloak';
import { useI18n } from '../i18n/I18nProvider';

const AuthContext = createContext({ enabled: false, ready: true, authenticated: true, roles: new Set(), logout: () => {} });

export function AuthProvider({ children }) {
  const [state, setState] = useState({ ready: !authEnabled, authenticated: !authEnabled, roles: new Set(), account: null, error: '' });

  useEffect(() => {
    if (!authEnabled) return undefined;
    let active = true;
    let applicationSessionRejected = false;
    const keycloak = keycloakClient();
    setAccessTokenProvider(async () => {
      if (!keycloak.authenticated) return null;
      try { await keycloak.updateToken(30); return keycloak.token || null; }
      catch (_) { return null; }
    });
    const rejectApplicationSession = () => {
      // A valid Keycloak session alone is insufficient: it must map to an
      // active legacy `accounts` record. Do not leave the operational shell
      // visible when that check fails.
      applicationSessionRejected = true;
      // clearToken may cause Keycloak to invoke the expiry hook. Disable the
      // automatic re-login first; a rejected account must stop on the error
      // screen instead of cycling through the SSO redirect forever.
      keycloak.onTokenExpired = undefined;
      keycloak.clearToken();
      if (active) setState({ ready: true, authenticated: false, roles: new Set(), error: 'account-unavailable' });
    };
    // Do not renew an access token merely because an unused browser tab is
    // still open. API calls request a fresh token through setAccessTokenProvider
    // above; this preserves an active working session while allowing Keycloak's
    // 12-hour idle timeout to require a new sign-in after inactivity.
    keycloak.onTokenExpired = () => {};
    window.addEventListener(AUTH_INVALID_EVENT, rejectApplicationSession);
    // Do not use Keycloak's `login-required` startup mode. An unknown or
    // orphaned SSO session would otherwise bounce browser ↔ Keycloak forever.
    // We silently discover a valid session here; the explicit sign-in button
    // starts the credential flow when it is actually needed.
    const startupTimer = window.setTimeout(() => {
      initializeKeycloak('check-sso').then(async authenticated => {
        if (!active) return;
        if (!authenticated) {
          setState({ ready: true, authenticated: false, roles: new Set(), error: '' });
          return;
        }
        try {
          // Verify the server-side account mapping before rendering any app
          // navigation or data. This covers orphaned/test Keycloak users.
          const identity = await api.me();
          if (active) setState({ ready: true, authenticated: true, roles: new Set(keycloak.realmAccess?.roles || []), account: identity.account || null, error: '' });
        } catch (error) {
          if (!active) return;
          // A 401/403 means an unusable identity or account mapping. A
          // transport/server failure should not be presented as an account
          // problem.
          if (/invalid|expired|authentication|required|forbidden/i.test(error?.message || "")) rejectApplicationSession();
          else setState({ ready: true, authenticated: false, roles: new Set(), error: 'service-unavailable' });
        }
      }).catch(() => active && setState({ ready: true, authenticated: false, roles: new Set(), error: 'service-unavailable' }));
    }, 700);
    return () => { active = false; window.clearTimeout(startupTimer); window.removeEventListener(AUTH_INVALID_EVENT, rejectApplicationSession); setAccessTokenProvider(async () => null); };
  }, []);

  const value = useMemo(() => ({ ...state, enabled: authEnabled, hasRole: role => state.roles.has(role),
    // Ask Keycloak to show the credential form even if an unusable SSO
    // session remains in the browser. This lets a dispatcher recover by
    // choosing a different mapped DriverOS account.
    login: () => keycloakClient()?.login({ redirectUri: window.location.origin, prompt: 'login' }),
    logout: () => keycloakClient()?.logout({ redirectUri: window.location.origin }) }), [state]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() { return useContext(AuthContext); }

export function AuthGate({ roles, children }) {
  const auth = useAuth();
  const { t } = useI18n();
  if (!auth.enabled) return children;
  if (!auth.ready) return <main><section className="page"><p className="status">{t('auth.connecting')}</p></section></main>;
  if (!auth.authenticated) return <main><section className="page"><p className="status error">{auth.error === 'account-unavailable' ? t('auth.accountUnavailable') : auth.error === 'service-unavailable' ? t('auth.serviceUnavailable') : t('auth.required')}</p><button className="project-create-button" type="button" onClick={auth.login}>{t('auth.returnToSignIn')}</button></section></main>;
  if (!roles.some(role => auth.hasRole(role))) return <main><section className="page"><p className="status error">{t('auth.forbidden')}</p><button onClick={auth.logout}>{t('common.signOut')}</button></section></main>;
  return children;
}
